import classnames from 'classnames';
import OkResetWaitButtonGroup from 'components/Common/OkResetWaitButtonGroup';
import { Tooltip } from 'components/tooltip/Tooltip';
import _get from 'lodash/get';
import _isNumber from 'lodash/isNumber';
import React, { SyntheticEvent } from 'react';
import intl from 'react-intl-universal';
import * as styles from './MaxInactiveDays.scss';

interface Props {
  error?: string;
  getInactiveDays: () => void;
  isMaxInactiveDaysBusy: boolean;
  maxInactiveDays: number | undefined;
  handleOpenConfirmExpiration: (payload: object) => void;
  maxInactiveDaysError?: string;
}

interface State {
  validationError?: string;
  maxInactiveDays: string;
}

const getInputString = (input: number | undefined) => {
  return (
    _isNumber(input) && !isNaN(input)
      ? input.toString()
      : ''
  );
};

const getInputNumber = (input: string) => {
  const num = parseInt(input, 10);
  return (
    _isNumber(num) && !isNaN(num)
      ? num
      : 0
    );
};

const isDirty = (maxInactiveDays: string, lastSavedMaxInactiveDays: number | undefined) => {
  return maxInactiveDays !== getInputString(lastSavedMaxInactiveDays);
};

const isValid = (value: string) => {
  const maxInactiveDays = parseInt(value, 10);
  return _isNumber(maxInactiveDays) && !isNaN(maxInactiveDays) && (maxInactiveDays <= 365);
};

class MaxInactiveDays extends React.PureComponent<Props, State> {

  readonly state: State = {
    validationError: undefined,
    maxInactiveDays: getInputString(this.props.maxInactiveDays)
  };

  private warnUnsavedChange: number;

  componentDidMount() {
    this.props.getInactiveDays();
  }

  componentWillUnmount() {
    window.clearTimeout(this.warnUnsavedChange);
  }

  canSave = () => {
    const { isMaxInactiveDaysBusy, maxInactiveDays: lastSavedMaxInactiveDays } = this.props;
    const { maxInactiveDays } = this.state;
    return !!(!isMaxInactiveDaysBusy && isDirty(maxInactiveDays, lastSavedMaxInactiveDays) && isValid(maxInactiveDays));
  }

  handleBlur = (dirty: boolean) => {
    if (dirty) {
      this.warnUnsavedChange = window.setTimeout(
        () => {
          this.setState({ validationError: intl.get('maxInactiveDaysNotSaved') });
        },
        100
      );
    }
  }

  handleMaxInactiveDaysChange = (event: SyntheticEvent<HTMLInputElement>) => {
    const newValue = (_get(event, 'target.value') || '').replace(/[^\d]/g, '');
    const error = isValid(newValue)
      ? undefined
      : intl.get('maxInactiveDaysValidation');
    this.setState({ validationError: error, maxInactiveDays: newValue });
  }

  handleReset = () => {
    window.clearTimeout(this.warnUnsavedChange);
    this.setState({
      maxInactiveDays: getInputString(this.props.maxInactiveDays),
      validationError: undefined
    });
  }

  handleSubmit = () => {
    this.setState({ validationError: undefined }, () => {
      window.clearTimeout(this.warnUnsavedChange);
      this.props.handleOpenConfirmExpiration({
        requestedNumberOfDays: getInputNumber(this.state.maxInactiveDays),
        cancelCallback: this.handleReset
      });
    });
  }

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.maxInactiveDays && nextProps.maxInactiveDays !== this.props.maxInactiveDays) {
      this.setState({ maxInactiveDays: getInputString(nextProps.maxInactiveDays) });
    }
  }

  render() {
    const { isMaxInactiveDaysBusy, maxInactiveDays: lastSavedMaxInactiveDays, error } = this.props;
    const { validationError, maxInactiveDays } = this.state;
    const dirty = isDirty(maxInactiveDays, lastSavedMaxInactiveDays);
    return (
      <div className={classnames(styles.container, isMaxInactiveDaysBusy && styles.isBusy)}>
        <label className="label">{intl.get('inactiveMeetingExpiration')}</label>
        <div className={styles.inputField}>
          <Tooltip
            text={intl.get('maxInactiveDaysValidation')}
          >
            <input
              id="inputMaxInactiveDays"
              maxLength={3}
              name="expiration"
              onBlur={() => this.handleBlur(dirty)}
              onChange={this.handleMaxInactiveDaysChange}
              value={maxInactiveDays}
            />
          </Tooltip>
          {dirty &&
            <OkResetWaitButtonGroup
              isSubmitting={isMaxInactiveDaysBusy}
              hasErrors={!this.canSave()}
              isAssociatedFieldTouched={true}
              handleReset={this.handleReset}
              handleSubmit={this.handleSubmit}
            />
          }
          {error && !validationError &&
            <div className={styles.error}>{intl.get('maxInactiveDaysAPIError')}</div>
          }
          {validationError &&
          <div className={styles.error}>{validationError}</div>
          }
        </div>
      </div>
    );
  }
}

export default MaxInactiveDays;
